/**
 * DocLocaleContext — React context for documentation language selection.
 *
 * Provides the current documentation locale and a setter.
 * Persists to localStorage so the choice survives page reloads.
 *
 * Supported locales: en, fr, de, it
 * Default: 'en'
 *
 * Usage:
 *   import { DocLocaleProvider, useDocLocale } from '@/uptk/core/context/doc-locale-context';
 *
 *   // In App.tsx or Layout:
 *   <DocLocaleProvider>
 *     <HelpDrawer />
 *     <DocPortalPage />
 *   </DocLocaleProvider>
 *
 *   // In any component:
 *   const { locale, setLocale } = useDocLocale();
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

// ─── Types ──────────────────────────────────────────────────────────

export type DocLocale = 'en' | 'fr' | 'de' | 'it' | 'ar';

export const DOC_LOCALES: Array<{ code: DocLocale; label: string; flag: string }> = [
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { code: 'it', label: 'Italiano', flag: '🇮🇹' },
  { code: 'ar', label: 'العربية', flag: '🇸🇦' },
];

const STORAGE_KEY = 'doc-locale';

interface DocLocaleContextValue {
  /** Current documentation locale */
  locale: DocLocale;
  /** Change the documentation locale */
  setLocale: (locale: DocLocale) => void;
  /** Get the display label for a locale code */
  getLabel: (code: DocLocale) => string;
  /** Get the flag emoji for a locale code */
  getFlag: (code: DocLocale) => string;
}

// ─── Context ────────────────────────────────────────────────────────

export const DocLocaleContext = createContext<DocLocaleContextValue | null>(null);

// ─── Helpers ────────────────────────────────────────────────────────

function isDocLocale(value: string): value is DocLocale {
  return ['en', 'fr', 'de', 'it', 'ar'].includes(value);
}

function readStoredLocale(): DocLocale {
  // UPT-1705: the portal language is the user's explicit app-wide choice — the
  // documentation follows it whenever it is set. The doc-local selection only
  // covers public visitors who picked a doc language before signing in.
  try {
    const portal = (localStorage.getItem('locale') ?? '').split(/[-_]/)[0];
    if (portal && isDocLocale(portal)) return portal;
  } catch {
    // localStorage not available
  }

  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && isDocLocale(stored)) return stored;
  } catch {
    // localStorage not available
  }

  // Try browser language
  try {
    const browserLang = navigator.language.split(/[-_]/)[0];
    if (isDocLocale(browserLang)) return browserLang;
  } catch {
    // navigator not available
  }

  return 'en';
}

// ─── Provider ────────────────────────────────────────────────────────

export function DocLocaleProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocaleState] = useState<DocLocale>(readStoredLocale);

  const setLocale = useCallback((newLocale: DocLocale) => {
    setLocaleState(newLocale);
    try {
      localStorage.setItem(STORAGE_KEY, newLocale);
    } catch {
      // localStorage not available
    }
  }, []);

  const getLabel = useCallback((code: DocLocale) => {
    return DOC_LOCALES.find((l) => l.code === code)?.label ?? code.toUpperCase();
  }, []);

  const getFlag = useCallback((code: DocLocale) => {
    return DOC_LOCALES.find((l) => l.code === code)?.flag ?? '🌐';
  }, []);

  return (
    <DocLocaleContext.Provider value={{ locale, setLocale, getLabel, getFlag }}>
      {children}
    </DocLocaleContext.Provider>
  );
}

// ─── Hook ────────────────────────────────────────────────────────────

export function useDocLocale(): DocLocaleContextValue {
  const ctx = useContext(DocLocaleContext);
  if (!ctx) {
    throw new Error('useDocLocale must be used within a <DocLocaleProvider>');
  }
  return ctx;
}

/**
 * Get the current doc locale without requiring the provider.
 * Falls back to 'en' if the provider is not mounted.
 * Useful for hooks that need the locale but may be used outside
 * the documentation section.
 */
export function useDocLocaleSafe(): DocLocale {
  const ctx = useContext(DocLocaleContext);
  return ctx?.locale ?? 'en';
}
